// cSpell:ignore xyflow Silae SILAE rubrique cumul
import { type NodeProps, type Node } from "@xyflow/react";
import {
  Award,
  Calculator,
  CalendarRange,
  Database,
  Gauge,
  MessageSquareText,
  Tag,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { SilaeFormatters } from "@/types/silae-formatters";
import type { SilaeSourceFormData } from "@/types/silae";
import { useSilaeFormatters } from "@/hooks/useSilaeFormatters";
import type { GraphNodeData } from "../utils/buildGraph";
import { logicalOperatorLabel } from "../utils/logicalOperatorLabel";
import { RichFormattedText } from "../utils/RichFormattedText";
import { ComplexExpressionTree } from "../utils/ComplexExpressionTree";
import { NodeSection } from "./NodeSection";
import { NodeShell, type NodeVariant } from "./NodeShell";
import { Sheet } from "lucide-react";

const BRANCH_VARIANT: Record<"if" | "else" | "main", NodeVariant> = {
  if: "bonus-if",
  else: "bonus-else",
  main: "bonus-main",
};

/** Couleur du tronc vertical reliant les disques d'icônes (assortie à la branche). */
const BRANCH_TRUNK: Record<"if" | "else" | "main", string> = {
  if: "bg-emerald-200 dark:bg-emerald-800/60",
  else: "bg-orange-200 dark:bg-orange-800/60",
  main: "bg-sky-200 dark:bg-sky-800/60",
};

const PILL =
  "inline-flex items-center rounded-md border border-zinc-200/80 dark:border-zinc-700/80 bg-white dark:bg-zinc-950 px-1.5 py-0.5 font-mono text-[10px] text-zinc-800 dark:text-zinc-100 shadow-[0_1px_0_rgba(0,0,0,0.03)]";

export function BonusNode({ data }: NodeProps<Node<GraphNodeData>>) {
  const { t } = useTranslation();
  const f: SilaeFormatters = useSilaeFormatters();

  const bonus = data.bonus;
  const branch = data.branch ?? "main";
  const variant = BRANCH_VARIANT[branch];
  const trunk = BRANCH_TRUNK[branch];

  if (!bonus) return null;

  const title =
    bonus.libelle?.trim() ||
    `${t("step5Validation.bonus")} ${(data.bonusIndex ?? 0) + 1}`;

  const sectionLabels = {
    rubrique: t("step5Validation.formatters.bonus.labels.rubrique", "Rubrique"),
    calcul: t("step5Validation.formatters.bonus.section.calcul", "Calcul"),
    stockage: t(
      "step5Validation.formatters.bonus.section.stockage",
      "Stockage",
    ),
    memos: t("step5Validation.formatters.bonus.section.memos", "Mémos"),
    counter: t("step5Validation.formatters.bonus.section.counter", "Compteur"),
    dsn: t("step5Validation.formatters.bonus.section.dsn", "Périodicité DSN"),
  };

  const labels = {
    method: t(
      "step5Validation.formatters.bonus.labels.method",
      "Méthode de calcul",
    ),
    bases: t("step5Validation.formatters.bonus.labels.bases", "Bases"),
    tauxs: t("step5Validation.formatters.bonus.labels.tauxs", "Taux"),
    variable: t("step5Validation.formatters.bonus.labels.variable", "Variable"),
    valueType: t("step5Validation.formatters.bonus.labels.valueType", "Valeur"),
    expression: t(
      "step5Validation.formatters.bonus.labels.expression",
      "Expression",
    ),
    counterType: t(
      "step5Validation.formatters.bonus.labels.counterType",
      "Compteur",
    ),
    dateRange: t(
      "step5Validation.formatters.bonus.labels.dateRange",
      "Période",
    ),
    bloc52: t("step5Validation.formatters.bonus.labels.bloc52", "Bloc 52"),
    evpForce: t(
      "step5Validation.formatters.bonus.labels.evpForce",
      "Forçage via EVP",
    ),
    source: t("step5Validation.formatters.bonus.labels.source", "Source"),
    sourceConditions: t(
      "step5Validation.formatters.bonus.labels.sourceConditions",
      "Si",
    ),
    sourceElse: t(
      "step5Validation.formatters.bonus.labels.sourceElse",
      "Sinon",
    ),
    sourceThen: t(
      "step5Validation.formatters.bonus.labels.sourceThen",
      "Alors",
    ),
    sourceAlternative: t(
      "step5Validation.formatters.bonus.labels.sourceAlternative",
      "Source alternative",
    ),
    sourcePrincipal: t(
      "step5Validation.formatters.bonus.labels.sourcePrincipal",
      "Source principale",
    ),
  } as const;

  const rubriqueLabel = f.findRubriqueLabel(bonus.rubriqueNumber);
  const rubriqueText =
    rubriqueLabel && rubriqueLabel !== bonus.rubriqueNumber
      ? rubriqueLabel
      : "";

  const showTaux =
    (bonus.calculMethod === "1" || bonus.calculMethod === "3") &&
    bonus.tauxs &&
    bonus.tauxs.length > 0;

  const memoLines: string[] = bonus.enableMemos
    ? f.formatBonusMemoLines(bonus.memoLines)
    : [];

  const bloc52 = bonus.enableDsnPeriodicity
    ? f.formatBonusBloc52(bonus.bloc52Code, bonus.bloc52CustomCode)
    : null;

  const dsnRange = bonus.enableDsnPeriodicity
    ? f.formatBonusDsnDateRange(bonus.dsnDateDebut, bonus.dsnDateFin)
    : "";

  return (
    <NodeShell
      variant={variant}
      icon={Award}
      title={title}
      targetStep={data.targetStep}
      hideBottomHandle
    >
      <div className="relative">
        {/* Tronc vertical (mind-map spine) — derrière les disques d'icônes */}
        <div
          className={`pointer-events-none absolute left-3.5 top-3 bottom-3 w-px -translate-x-1/2 ${trunk}`}
          aria-hidden="true"
        />

        <div className="relative space-y-3">
          {/* Rubrique — toujours présente */}
          <NodeSection label={sectionLabels.rubrique} icon={Tag} tone="neutral">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className={PILL}>{bonus.rubriqueNumber}</span>
              {rubriqueText && (
                <span className="text-zinc-700 dark:text-zinc-200">
                  {rubriqueText}
                </span>
              )}
            </div>
          </NodeSection>

          {/* Calcul */}
          <NodeSection
            label={sectionLabels.calcul}
            icon={Calculator}
            tone="indigo"
          >
            <FactRow label={labels.method}>
              <span className="font-medium">
                {f.formatCalculMethod(bonus.calculMethod)}
              </span>
            </FactRow>
            {bonus.bases && bonus.bases.length > 0 && (
              <FactRow label={labels.bases}>
                <SourceList
                  sources={bonus.bases}
                  formatters={f}
                  typeLabel={labels.bases}
                  evpForceLabel={labels.evpForce}
                  conditionsLabel={labels.sourceConditions}
                  elseLabel={labels.sourceElse}
                  alternativeLabel={labels.sourceAlternative}
                  principalLabel={labels.sourcePrincipal}
                  thenLabel={labels.sourceThen}
                  t={t}
                />
              </FactRow>
            )}
            {showTaux && (
              <FactRow label={labels.tauxs}>
                <SourceList
                  sources={bonus.tauxs}
                  formatters={f}
                  typeLabel={labels.tauxs}
                  evpForceLabel={labels.evpForce}
                  conditionsLabel={labels.sourceConditions}
                  elseLabel={labels.sourceElse}
                  alternativeLabel={labels.sourceAlternative}
                  principalLabel={labels.sourcePrincipal}
                  thenLabel={labels.sourceThen}
                  t={t}
                />
              </FactRow>
            )}
          </NodeSection>

          {/* Stockage */}
          {bonus.enableStockage && (
            <NodeSection
              label={sectionLabels.stockage}
              icon={Database}
              tone="violet"
            >
              {bonus.stockageVariableName && (
                <FactRow label={labels.variable}>
                  <span className={PILL}>{bonus.stockageVariableName}</span>
                </FactRow>
              )}
              <FactRow label={labels.valueType}>
                <span className="font-medium">
                  {f.formatBonusValueType(bonus.stockageVariableType)}
                </span>
              </FactRow>
              {bonus.stockageVariableType === "custom" &&
                bonus.stockageCustomExpression && (
                  <FactRow label={labels.expression}>
                    <CodeBlock>{bonus.stockageCustomExpression}</CodeBlock>
                  </FactRow>
                )}
            </NodeSection>
          )}

          {/* Mémos */}
          {bonus.enableMemos && memoLines.length > 0 && (
            <NodeSection
              label={sectionLabels.memos}
              icon={MessageSquareText}
              tone="emerald"
            >
              <div className="space-y-1.5">
                {memoLines.map((line: string, i: number) => (
                  <MemoLine
                    key={i}
                    line={line}
                    resolveLabel={f.resolveSilaeVariableLabel}
                  />
                ))}
              </div>
            </NodeSection>
          )}

          {/* Compteur */}
          {bonus.enableCounterFeeding && (
            <NodeSection label={sectionLabels.counter} icon={Gauge} tone="cyan">
              {bonus.counterType && (
                <FactRow label={labels.counterType}>
                  <span className="font-medium">
                    {f.formatBonusCounterType(bonus.counterType)}
                  </span>
                </FactRow>
              )}
              <FactRow label={labels.valueType}>
                <span className="font-medium">
                  {f.formatBonusValueType(bonus.counterValueType)}
                </span>
              </FactRow>
              {bonus.counterValueType === "custom" &&
                bonus.counterCustomExpression && (
                  <FactRow label={labels.expression}>
                    <CodeBlock>{bonus.counterCustomExpression}</CodeBlock>
                  </FactRow>
                )}
            </NodeSection>
          )}

          {/* Périodicité DSN */}
          {bonus.enableDsnPeriodicity && (
            <NodeSection
              label={sectionLabels.dsn}
              icon={CalendarRange}
              tone="rose"
            >
              {dsnRange && (
                <FactRow label={labels.dateRange}>
                  <span>{dsnRange}</span>
                </FactRow>
              )}
              {bloc52 && (
                <FactRow label={labels.bloc52}>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className={PILL}>{bloc52.pill}</span>
                    <span className="text-zinc-700 dark:text-zinc-200">
                      {bloc52.label}
                    </span>
                  </div>
                </FactRow>
              )}
            </NodeSection>
          )}
        </div>
      </div>
    </NodeShell>
  );
}

/** Une rangée fait/valeur dans un panneau de section. */
function FactRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 py-0.5 first:pt-0 last:pb-0">
      <span className="text-[9px] font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
        {label}
      </span>
      <div className="text-zinc-900 dark:text-zinc-50 wrap-break-word">
        {children}
      </div>
    </div>
  );
}

/** Pastille distincte pour les variables `{{var}}` — couleur contrastée
 *  contre le fond émeraude du panneau Mémos. */
const MEMO_VAR_PILL =
  "inline-flex items-center rounded-md border border-violet-300/80 dark:border-violet-700/70 bg-white dark:bg-zinc-950 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 dark:text-violet-300";

/**
 * Rend une ligne de mémo à la manière de l'aperçu du wizard :
 * — les segments `{{variable}}` sont remplacés par le libellé humain de la variable
 *   (résolu via le store SILAE) et affichés en pastille discrète,
 * — les lignes de tirets (`─` ou `-`, ≥ 10) deviennent un séparateur fin.
 */
function MemoLine({
  line,
  resolveLabel,
}: {
  line: string;
  resolveLabel: (code: string) => string;
}) {
  if (/^[-─]{10,}$/.test(line.trim())) {
    return (
      <div
        className="my-2 h-px w-full bg-emerald-300/60 dark:bg-emerald-700/50"
        aria-hidden="true"
      />
    );
  }

  const tokens = line.split(/(\{\{[A-Za-z_][A-Za-z0-9_.]*\}\})/);
  const hasVar = tokens.some((tok) =>
    /^\{\{[A-Za-z_][A-Za-z0-9_.]*\}\}$/.test(tok),
  );

  if (!hasVar) {
    return <span className="text-zinc-800 dark:text-zinc-100">{line}</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1 wrap-break-word">
      {tokens.map((tok, i) => {
        if (!tok) return null;
        const m = tok.match(/^\{\{([A-Za-z_][A-Za-z0-9_.]*)\}\}$/);
        if (m) {
          const code = m[1];
          const label = resolveLabel(code);
          return (
            <span key={i} className={MEMO_VAR_PILL} title={code}>
              {label}
            </span>
          );
        }
        return (
          <span key={i} className="text-zinc-700 dark:text-zinc-200">
            {tok}
          </span>
        );
      })}
    </div>
  );
}

function CodeBlock({ children }: { children: ReactNode }) {
  return (
    <code className="block rounded-md border border-zinc-200/80 dark:border-zinc-700/80 bg-white/70 dark:bg-zinc-950/70 px-2 py-1 font-mono text-[10px] text-zinc-800 dark:text-zinc-100 wrap-break-word">
      {children}
    </code>
  );
}

interface SourceLabels {
  evpForce: string;
  type: string;
  conditions: string;
  else: string;
  then: string;
  alternative: string;
  principal: string;
}

/**
 * Liste de sources (bases ou taux) — chaque source est rendue comme une carte
 * indépendante via `SourceCard`. Les sources sont empilées verticalement ;
 * l'addition est portée par la disposition, sans connecteur explicite.
 */
function SourceList({
  sources,
  formatters,
  evpForceLabel,
  typeLabel,
  conditionsLabel,
  elseLabel,
  alternativeLabel,
  principalLabel,
  thenLabel,
  t,
}: {
  sources: SilaeSourceFormData[];
  formatters: SilaeFormatters;
  evpForceLabel: string;
  typeLabel: string;
  conditionsLabel: string;
  elseLabel: string;
  alternativeLabel: string;
  principalLabel: string;
  thenLabel: string;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  const sourceLabels: SourceLabels = {
    evpForce: evpForceLabel,
    type: typeLabel,
    conditions: conditionsLabel,
    else: elseLabel,
    then: thenLabel,
    alternative: alternativeLabel,
    principal: principalLabel,
  };

  return (
    <ul className="space-y-1.5">
      {sources.map((source, i) => (
        <li key={source.id ?? i}>
          <SourceCard
            source={source}
            index={i}
            total={sources.length}
            formatters={formatters}
            labels={sourceLabels}
            t={t}
          />
        </li>
      ))}
    </ul>
  );
}

/**
 * Wrapper léger pour une source (base ou taux). Composition :
 *  - en-tête avec le type de source (Bases / Taux) et l'index numéroté (≥ 2),
 *  - bloc "Si" listant les conditions de la source (quand `hasConditions`),
 *  - panneau "Alors" via `SourceBranchPanel` (source principale),
 *  - panneau "Sinon" via `SourceBranchPanel` (source alternative, quand
 *    `elseSource` non nulle).
 */
function SourceCard({
  source,
  index,
  total,
  formatters,
  labels,
  t,
}: {
  source: SilaeSourceFormData;
  index: number;
  total: number;
  formatters: SilaeFormatters;
  labels: SourceLabels;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  const conditions = source.hasConditions ? (source.conditions ?? []) : [];
  const hasSourceConditions = conditions.length > 0;
  const elseSource = source.elseSource ?? null;

  const showHeader = total > 1;

  return (
    <div className="rounded-md border-l-2 border-indigo-300/80 dark:border-indigo-700/70 bg-white dark:bg-zinc-950 px-2 py-1.5">
      {/* En-tête : type de source + index — affiché seulement quand on a
       *  plusieurs sources, sinon le type est déjà donné par la FactRow parente. */}
      {showHeader && (
        <div className="mb-1 flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
          {`${labels.type} ${index + 1}`}
        </div>
      )}

      {/* "Si" — conditions de la source (sans wrapper, juste libellé + liste) */}
      {hasSourceConditions && (
        <div className="mb-1.5">
          <div className="mb-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-500 dark:text-amber-300">
            {labels.conditions}
          </div>
          <ul className="space-y-0.5">
            {conditions.map((cond, ci) => (
              <li key={cond.id} className="flex flex-col gap-0.5">
                <div className="flex items-start gap-1.5">
                  <span
                    className="mt-1 h-1 w-1 shrink-0 rounded-full bg-amber-500 dark:bg-amber-400"
                    aria-hidden="true"
                  />
                  <div className="flex-1 wrap-break-word">
                    <RichFormattedText
                      text={formatters.formatConditionDescription(cond)}
                    />
                  </div>
                </div>
                {ci < conditions.length - 1 && (
                  <span className="ml-2.5 text-[9px] font-bold uppercase tracking-wider text-amber-500 dark:text-amber-400">
                    {logicalOperatorLabel(source.conditionsOperators?.[ci], t)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Source principale : sans conditions ni branche alternative, on rend
       *  la formule directement dans le wrapper indigo (pas de panneau vert).
       *  Sinon, on encapsule dans un panneau "Alors" pour matérialiser le
       *  pendant du "Sinon" qui suit. */}
      {hasSourceConditions || elseSource ? (
        <SourceBranchPanel
          source={source}
          isAlternative={false}
          showThenHeader={Boolean(elseSource)}
          labels={labels}
          formatters={formatters}
        />
      ) : (
        <BareSourceContent
          source={source}
          labels={labels}
          formatters={formatters}
        />
      )}

      {/* Panneau "Sinon" — source alternative */}
      {elseSource && (
        <div className="mt-1.5">
          <SourceBranchPanel
            source={elseSource}
            isAlternative={true}
            showThenHeader
            labels={labels}
            formatters={formatters}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Panneau dédié pour les sections "Alors" (source principale) et "Sinon"
 * (source alternative) à l'intérieur d'une `SourceCard`. Affiche :
 *  - un en-tête (`ALORS · SOURCE PRINCIPALE` ou `SINON · SOURCE ALTERNATIVE`),
 *  - la formule de la source (sans la mention `[Forcé via EVP: …]` inline),
 *  - une pastille "Forçage via EVP" quand activée.
 *
 * Les couleurs (verts pour principale, oranges pour alternative) miroir l'une
 * de l'autre — même structure, même densité, simple bascule de palette.
 */
function SourceBranchPanel({
  source,
  isAlternative,
  showThenHeader,
  labels,
  formatters,
}: {
  source: SilaeSourceFormData;
  isAlternative: boolean;
  showThenHeader: boolean;
  labels: SourceLabels;
  formatters: SilaeFormatters;
}) {
  const display = formatters.formatSourceDisplay(source, {
    skipEvpForce: true,
  });
  const hasEvpForce =
    source.enableEvpForce && (source.evpForceTitle ?? "").trim().length > 0;

  const palette = isAlternative
    ? {
        wrapper:
          "border-orange-500/90 dark:border-orange-500/80 bg-orange-50/60 dark:bg-orange-950/25",
        headerStrong: "text-orange-800 dark:text-orange-300",
        headerMuted: "text-orange-700/70 dark:text-orange-400/70",
      }
    : {
        wrapper:
          "border-emerald-300/80 dark:border-emerald-700/70 bg-emerald-50/50 dark:bg-emerald-950/20",
        headerStrong: "text-emerald-700 dark:text-emerald-300",
        headerMuted: "text-emerald-600/70 dark:text-emerald-400/70",
      };

  const headerStrongText = isAlternative ? labels.else : labels.then;
  const headerMutedText = isAlternative ? labels.alternative : labels.principal;

  return (
    <div className={`rounded-md border-l-2 px-2 py-1.5 ${palette.wrapper}`}>
      {showThenHeader && (
        <div className="mb-1 flex items-center gap-1 text-[9px] uppercase tracking-wide">
          <span className={`font-semibold ${palette.headerStrong}`}>
            {headerStrongText}
          </span>
          <span className={`font-normal ${palette.headerMuted}`}>
            · {headerMutedText}
          </span>
        </div>
      )}
      <div className="wrap-break-word text-zinc-900 dark:text-zinc-50">
        {source.sourceType === "complex_expression" ? (
          <ComplexExpressionTree source={source} formatters={formatters} />
        ) : (
          <RichFormattedText text={display} />
        )}
      </div>
      {hasEvpForce && (
        <div className="mt-1 inline-flex items-center gap-1 rounded-md border border-violet-300/70 dark:border-violet-700/60 bg-violet-50/70 dark:bg-violet-950/40 px-1 py-0.5">
          <Sheet
            className=" shrink-0 text-violet-700 dark:text-violet-300"
            strokeWidth={1.5}
            size={16}
            aria-hidden="true"
          />
          <span className="text-[8px] font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">
            {labels.evpForce}
          </span>
          <span className="inline-flex items-center rounded border border-violet-300/80 dark:border-violet-700/70 bg-white dark:bg-zinc-950 px-1 py-px font-mono text-[9px] text-violet-700 dark:text-violet-300">
            {source.evpForceTitle}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Contenu nu d'une source — utilisé quand la source n'a ni conditions ni
 * branche alternative : pas de sémantique "Alors / Sinon" à matérialiser,
 * donc on rend la formule directement dans le wrapper de `SourceCard` sans
 * panneau vert. La pastille "Forçage via EVP" reste affichée si activée.
 */
function BareSourceContent({
  source,
  labels,
  formatters,
}: {
  source: SilaeSourceFormData;
  labels: SourceLabels;
  formatters: SilaeFormatters;
}) {
  const display = formatters.formatSourceDisplay(source, {
    skipEvpForce: true,
  });
  const hasEvpForce =
    source.enableEvpForce && (source.evpForceTitle ?? "").trim().length > 0;

  return (
    <>
      <div className="wrap-break-word text-zinc-900 dark:text-zinc-50">
        {source.sourceType === "complex_expression" ? (
          <ComplexExpressionTree source={source} formatters={formatters} />
        ) : (
          <RichFormattedText text={display} />
        )}
      </div>
      {hasEvpForce && (
        <div className="mt-1 inline-flex items-center gap-1 rounded-md border border-violet-300/70 dark:border-violet-700/60 bg-violet-50/70 dark:bg-violet-950/40 px-1 py-0.5">
          <Sheet
            className=" shrink-0 text-violet-700 dark:text-violet-300"
            strokeWidth={1.5}
            size={16}
            aria-hidden="true"
          />
          <span className="text-[8px] font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">
            {labels.evpForce}
          </span>
          <span className="inline-flex items-center rounded border border-violet-300/80 dark:border-violet-700/70 bg-white dark:bg-zinc-950 px-1 py-px font-mono text-[9px] text-violet-700 dark:text-violet-300">
            {source.evpForceTitle}
          </span>
        </div>
      )}
    </>
  );
}
